import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import ConditionsMarquee from "@/components/ConditionsMarquee";
import ReserveSlot from "@/components/ReserveSlot";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { physicianJsonLd, localBusinessJsonLd, websiteJsonLd, medicalOrganizationJsonLd, breadcrumbListJsonLd } from "@/data/schema";
import { useContent } from "@/context/ContentContext";
import { WHATSAPP_LINK } from "@/lib/api";
import { PORTRAITS, PORTRAIT_ALT } from "@/data/portraits";

const CHAPTERS = [
  {
    n: "01",
    title: "One doctor. Every step.",
    body: "You will not be handed off. Dr Sidhu personally leads your first call, your imaging review, your procedure, and every follow-up — because vascular decisions are not modular.",
  },
  {
    n: "02",
    title: "Interventional, not open-first.",
    body: "Endovascular, image-guided, keyhole techniques wherever they serve you better than open surgery. When they do not, we say so — and refer honestly.",
  },
  {
    n: "03",
    title: "Second opinions welcomed.",
    body: "A stent is not the only answer. A quiet 'wait-and-monitor' is often the right one. You'll leave with a plan you understand, not a procedure you were sold.",
  },
  {
    n: "04",
    title: "Continuity across borders.",
    body: "NRI patients receive full pre-arrival video consults, coordinated hospital admission, and post-return follow-up — the same doctor from Toronto to Mohali to Toronto.",
  },
];

export default function Home() {
  const { content } = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const h = content.hero;

  return (
    <div data-testid="home-page">
      <SEO
        title="Dr Tegbir Vascular — Senior-led Vascular & Endovascular IR, Mohali"
        description="Dr Tegbir Singh Sidhu — Principal Consultant, Interventional Radiology at Max Super Speciality Hospital, Mohali. Continuity from first call to final follow-up. 34 vascular conditions treated. NRI patients served across Canada, USA, UK, Australia, UAE, Saudi Arabia."
        keywords={["interventional radiology mohali", "vascular specialist chandigarh", "endovascular treatment india", "Dr Tegbir Sidhu", "aortic aneurysm india", "uterine fibroid embolisation", "prostate artery embolisation"]}
      />
      <JsonLd data={[
        websiteJsonLd(),
        medicalOrganizationJsonLd(),
        physicianJsonLd(),
        localBusinessJsonLd(),
        breadcrumbListJsonLd([{ label: "Home", to: "/" }]),
      ]}/>
      {/* HERO */}
      <section ref={heroRef} className="relative pt-28 md:pt-36 pb-24 md:pb-40 overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6">
            {/* Overline */}
            <FadeUp className="col-span-12" delay={0.1}>
              <div className="flex items-center gap-3 kbd">
                <span className="inline-block w-8 h-px bg-ink/60" />
                {h.overline}
              </div>
            </FadeUp>

            {/* Left: massive headline */}
            <div className="col-span-12 md:col-span-8 mt-10 md:mt-14">
              <h1 className="font-serif text-[13vw] md:text-[9.2vw] lg:text-[8vw] leading-[0.92] tracking-tightest text-ink">
                <MaskedReveal text={h.line1} delay={0.2} />
                <MaskedReveal text={h.line2} delay={0.3} />
                <span className="block">
                  <MaskedReveal text={h.line3} delay={0.4} className="italic font-normal" />
                </span>
              </h1>
            </div>

            {/* Right: portrait with parallax + spotlight frame */}
            <div className="col-span-12 md:col-span-4 md:pl-8 mt-12 md:mt-0 self-end">
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[3/4] overflow-hidden hairline bg-bone-soft"
              >
                <motion.img
                  src={PORTRAITS.HERO}
                  alt={PORTRAIT_ALT}
                  style={{ y: portraitY, scale: portraitScale }}
                  className="w-full h-full object-cover"
                />
                <div className="noise-overlay absolute inset-0" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-bone">
                  <div>
                    <div className="kbd text-white/80">Principal Consultant</div>
                    <div className="font-serif text-xl mt-1 leading-tight">Dr Tegbir Singh Sidhu</div>
                  </div>
                  <div className="kbd text-white/70">MOH · IND</div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              </motion.div>
            </div>

            {/* Sub + CTAs */}
            <FadeUp className="col-span-12 md:col-span-7 mt-16" delay={0.9}>
              <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-2xl">{h.sub}</p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-pill-inv group">
                  {h.cta_primary}
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link to="/urgent" data-testid="hero-cta-urgent" className="btn-crimson">
                  {h.cta_secondary}
                  <ArrowRight size={14} />
                </Link>
              </div>
            </FadeUp>

            {/* Reserve-a-slot widget */}
            <div className="col-span-12 md:col-span-8 mt-8">
              <ReserveSlot/>
            </div>

            {/* Meta strip */}
            <FadeUp className="col-span-12 mt-24" delay={1.1}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 hairline-t pt-8">
                {[
                  ["15+ yrs", "Interventional radiology"],
                  ["Max Mohali", "Principal consultant"],
                  ["34+", "Vascular conditions treated"],
                  ["6 countries", "NRI continuity"],
                ].map(([k, v], i) => (
                  <div key={i} className="pl-4 border-l border-black/10">
                    <div className="font-serif text-3xl md:text-4xl tracking-tightest">{k}</div>
                    <div className="kbd mt-2">{v}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CONDITIONS MARQUEE */}
      <ConditionsMarquee items={content.conditions} />

      {/* MANIFESTO CHAPTERS */}
      <section className="py-28 md:py-40" id="why">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-20 md:mb-32">
              <div>
                <div className="kbd mb-6">— A Manifesto</div>
                <h2 className="font-serif text-5xl md:text-7xl tracking-tightest leading-[1.02] max-w-3xl">
                  What makes a<br/>vascular decision<br/><span className="italic font-normal">a good one.</span>
                </h2>
              </div>
              <Link to="/why-choose" data-testid="manifesto-cta" className="btn-pill self-start md:self-end">
                Read the full case <ArrowUpRight size={14} />
              </Link>
            </div>
          </FadeUp>

          <div className="space-y-24 md:space-y-32">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={0.1 * i}>
                <div className={`grid grid-cols-12 gap-6 md:gap-10 items-start ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                  <div className="col-span-12 md:col-span-3 [direction:ltr]">
                    <div className="font-mono text-sm text-ink-muted">CHAPTER</div>
                    <div className="font-serif text-[10rem] md:text-[12rem] leading-none tracking-tightest text-crimson">{c.n}</div>
                  </div>
                  <div className="col-span-12 md:col-span-9 [direction:ltr]">
                    <h3 className="font-serif text-4xl md:text-6xl tracking-tightest leading-[1.05] max-w-3xl">
                      <MaskedReveal text={c.title} stagger={0.05} />
                    </h3>
                    <p className="mt-8 text-base md:text-lg text-ink-muted leading-relaxed max-w-2xl">
                      {c.body}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TEASER */}
      <section className="bg-[#0A0A0A] text-bone py-28 md:py-40">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6">
            <FadeUp className="col-span-12 md:col-span-5">
              <div className="kbd text-white/60">— How it works</div>
              <h2 className="mt-6 font-serif text-5xl md:text-7xl tracking-tightest leading-[1.02]">
                <MaskedReveal text={"From first"} />
                <MaskedReveal text={"WhatsApp"} delay={0.1} className="italic font-normal" />
                <MaskedReveal text={"to treatment."} delay={0.2} />
              </h2>
              <p className="mt-8 text-white/70 max-w-md leading-relaxed">
                A four-step continuity path. No call centres. No handoffs. One doctor from start to finish.
              </p>
              <Link to="/how-it-works" data-testid="process-cta" className="mt-10 inline-flex items-center gap-2 text-[13px] tracking-wide border border-white/30 rounded-full px-6 py-3 hover:bg-bone hover:text-ink transition-colors">
                See the full process <ArrowUpRight size={14} />
              </Link>
            </FadeUp>

            <div className="col-span-12 md:col-span-7 md:pl-10">
              <ol className="space-y-8">
                {[
                  ["01","Reach out","WhatsApp, form, or urgent path. Team responds within one working hour."],
                  ["02","Pre-screening","Clinical coordinator gathers imaging + history. Nothing repeated later."],
                  ["03","Video consult","Dr Sidhu personally reviews with you and family — no substitutes."],
                  ["04","Treatment & follow-up","At Max Mohali, with pre-arrival and post-departure continuity."],
                ].map(([n, t, d]) => (
                  <li key={n} className="grid grid-cols-12 gap-4 border-t border-white/10 pt-6">
                    <div className="col-span-2 md:col-span-1 font-mono text-white/50 text-sm">{n}</div>
                    <div className="col-span-10 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tightest">{t}</div>
                    <div className="col-span-12 md:col-span-7 text-white/70 leading-relaxed">{d}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-28 md:py-40">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12 md:col-span-8">
            <div className="kbd">— Start here</div>
            <h2 className="mt-6 font-serif text-5xl md:text-8xl tracking-tightest leading-[0.98]">
              <MaskedReveal text={"Speak to"} />
              <MaskedReveal text={"the doctor,"} delay={0.1} />
              <MaskedReveal text={"not the desk."} delay={0.2} className="italic font-normal" />
            </h2>
          </FadeUp>
          <FadeUp className="col-span-12 md:col-span-4 md:pl-8 self-end" delay={0.2}>
            <p className="text-ink-muted max-w-sm leading-relaxed">
              Prefer WhatsApp? Message the practice number for a same-day team response.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="closing-cta-form" className="btn-pill-inv">Book consultation <ArrowUpRight size={14} /></Link>
              <a data-testid="closing-cta-whatsapp" target="_blank" rel="noreferrer" href={WHATSAPP_LINK("Hello Dr Sidhu's team, I would like to speak to the doctor.")} className="btn-pill">WhatsApp <ArrowUpRight size={14} /></a>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
